import { ComponentType } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import type { IconSmallProps } from '@components/icons';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { Stack } from '@components/stack';
import { Tooltip } from '@components/tooltip';
import { theme } from '@styles/theme';

export type ActivityProps = {
  value: string | number;
  highlight?: boolean;
  small?: boolean;
  hasError?: boolean;
  icon?: ComponentType<IconSmallProps>;
  tooltip?: string;
  onClick?(): void;
};

export function Activity(props: ActivityProps) {
  const {
    icon,
    value,
    highlight = false,
    small = false,
    tooltip = '',
    onClick,
    hasError = false,
  } = props;

  const content = (
    <Container>
      <ActivityIcon icon={icon} onClick={onClick} />
      {hasError && <IconFormIndicatorError size={small ? 16 : 24} />}
      <Badge
        data-testid="badge"
        variant={highlight ? 'active' : 'secondary'}
        size={small ? 'sm' : 'md'}
        children={value}
      />
    </Container>
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return content;
}

type ActivityIconProps = Pick<ActivityProps, 'onClick' | 'icon'>;
function ActivityIcon(props: ActivityIconProps) {
  const { icon: Icon, onClick } = props;

  if (!Icon) return null;

  return (
    <IconContainer
      $clickable={Boolean(onClick)}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
    >
      <Icon color="purple60" />
    </IconContainer>
  );
}

const Container = styled(Stack).attrs({
  space: 1,
  orientation: 'horizontal',
  align: 'center',
})({
  display: 'inline-flex',
});

const IconContainer = styled.div<{ $clickable: boolean }>((p) => ({
  width: '1rem',
  height: '1rem',
  ...(p.$clickable && {
    '&:hover svg': {
      cursor: 'pointer',
      color: theme.color.elementBlue100,
    },
  }),
}));
